import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../data.service';

/**
 *  IKTATGATÓ – intézményi iratkezelés kliensoldali szolgáltatása.
 *
 *  Nem keverendő a CAESAR ügyvédi ügykezelővel: ott ügy → beadvány a rendezőelv,
 *  itt iktatókönyv → ügyirat (főszám) → irat (alszám).
 *
 *  A backend minden listát a hozzáférés szerint szűr, ezért a felhasználónevet
 *  minden híváshoz elküldjük (a szerver ezt a tokenből is kiolvassa, ez csak
 *  a fejlesztői/standalone eset miatt kell).
 */
@Injectable({ providedIn: 'root' })
export class IktatgatoService {
  private sajatIntezmeny: any = null;
  private felhasznalo = '';

  constructor(private http: HttpClient, private data: DataService) {}

  /*  Mindig a FRISS tárolt értéket olvassuk. A szolgáltatás az egész alkalmazásra egy
   *  példány: ha egyszer eltenné, intézményváltás vagy újrabelépés után is a régi
   *  intézménnyel és felhasználóval kérdezne – belépés közben pedig akár az üres,
   *  még be sem töltött értékkel. */
  private async betolt() {
    this.sajatIntezmeny = (await this.data.getFromStorage('sajatIntezmeny')) || this.sajatIntezmeny;
    const user = await this.data.getFromStorage('user');
    this.felhasznalo = String(user?.username || user?.adid || '') || this.felhasznalo;
  }

  async intezmeny(): Promise<string> {
    await this.betolt();
    return String(this.sajatIntezmeny?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
  }

  async ki(): Promise<string> {
    await this.betolt();
    return this.felhasznalo;
  }

  private async ut(vege: string, params: Record<string, any> = {}): Promise<string> {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== null && v !== undefined && v !== '') p.set(k, String(v));
    }
    p.set('felhasznalo', await this.ki());
    return `/api/iktatgato/${vege}?${p.toString()}`;
  }

  private async torzs(adat: any): Promise<any> {
    return { ...adat, felhasznalo: await this.ki() };
  }

  /* ── áttekintés ── */
  async attekintes(ev?: number) {
    return firstValueFrom(this.http.get<any>(await this.ut(`attekintes/${await this.intezmeny()}`, { ev })));
  }

  /* ── iktatókönyvek ── */
  async konyvek(ev?: number) {
    return firstValueFrom(this.http.get<any[]>(await this.ut(`konyvek/${await this.intezmeny()}`, { ev })));
  }
  async konyvLetrehoz(adat: any) {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/konyv/${await this.intezmeny()}`, await this.torzs(adat)));
  }
  async konyvMuvelet(id: string, adat: any) {
    return firstValueFrom(this.http.patch<any>(`/api/iktatgato/konyv/${id}`, await this.torzs(adat)));
  }

  /* ── érkeztetés ── */
  async erkeztetesek(ev?: number, csakNyitott = false) {
    return firstValueFrom(this.http.get<any[]>(
      await this.ut(`erkeztetes/${await this.intezmeny()}`, { ev, nyitott: csakNyitott ? 1 : '' })));
  }
  async erkeztet(adat: any) {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/erkeztetes/${await this.intezmeny()}`, await this.torzs(adat)));
  }
  /* ── hivatali kapu: beérkező küldemények és igazolások (BKSZ gépi interfész) ── */
  async bejovoBeallitas() {
    return firstValueFrom(this.http.get<any>(await this.ut(`bksz-bejovo/beallitas/${await this.intezmeny()}`)));
  }
  async bejovoBeallitasMent(adat: { enabled: boolean; intervallum_perc: number }) {
    return firstValueFrom(this.http.put<any>(
      `/api/iktatgato/bksz-bejovo/beallitas/${await this.intezmeny()}`, await this.torzs(adat)));
  }
  /** Letöltés indítása; a futás a háttérben megy, az állapotot a `bejovoStatusz` adja. */
  async bejovoSzinkron() {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/bksz-bejovo/szinkron/${await this.intezmeny()}`, await this.torzs({})));
  }
  async bejovoMegszakit() {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/bksz-bejovo/megszakit/${await this.intezmeny()}`, await this.torzs({})));
  }
  async bejovoStatusz() {
    return firstValueFrom(this.http.get<any>(await this.ut(`bksz-bejovo/statusz/${await this.intezmeny()}`)));
  }
  async bejovoLista(fajta = '', limit = 200, q = '') {
    return firstValueFrom(this.http.get<any[]>(
      await this.ut(`bksz-bejovo/lista/${await this.intezmeny()}`, { fajta, limit, q })));
  }
  /** Utólagos érkeztetés egy letöltött tételről – igazolásról is. Egyszer érkeztet. */
  async bejovoErkeztet(id: string, targy = '') {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/bksz-bejovo/erkeztet/${id}`, await this.torzs({ targy })));
  }
  bejovoFajlUrl(id: string) { return `/api/iktatgato/bksz-bejovo/fajl/${id}`; }

  /* ── iktatás ── */
  async iktat(adat: any) {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/iktatas/${await this.intezmeny()}`, await this.torzs(adat)));
  }
  async ugyiratok(szuro: Record<string, any> = {}) {
    return firstValueFrom(this.http.get<any>(await this.ut(`ugyiratok/${await this.intezmeny()}`, szuro)));
  }
  async ugyirat(id: string) {
    return firstValueFrom(this.http.get<any>(await this.ut(`ugyirat/${id}`)));
  }
  async ugyiratModosit(id: string, adat: any) {
    return firstValueFrom(this.http.patch<any>(`/api/iktatgato/ugyirat/${id}`, await this.torzs(adat)));
  }
  async ervenytelenit(tipus: 'ugyirat' | 'irat', id: string, indok: string) {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/ervenytelenites/${tipus}/${id}`, await this.torzs({ indok })));
  }

  /* ── irattári terv ── */
  async terv(ev?: number) {
    return firstValueFrom(this.http.get<any[]>(await this.ut(`terv/${await this.intezmeny()}`, { ev })));
  }
  async tervMent(ev: number, tetelek: any[]) {
    return firstValueFrom(this.http.put<any>(
      `/api/iktatgato/terv/${await this.intezmeny()}`, await this.torzs({ ev, tetelek })));
  }
  /** Az alapértelmezett (20/2012. EMMI r. 1. melléklet szerinti) terv összevetése – nem ír. */
  async tervAlap(ev: number) {
    return firstValueFrom(this.http.get<any>(await this.ut(`terv-alap/${await this.intezmeny()}`, { ev })));
  }
  /** A hiányzó alapértelmezett tételek felvétele (a meglévőkhöz nem nyúl). */
  async tervAlapBetolt(ev: number, kulcsok: string[]) {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/terv-alap/${await this.intezmeny()}`, await this.torzs({ ev, kulcsok })));
  }

  /* ── postakönyv ── */
  async postakonyv(statusz?: string) {
    return firstValueFrom(this.http.get<any[]>(await this.ut(`postakonyv/${await this.intezmeny()}`, { statusz })));
  }
  async postaraTesz(adat: any) {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/postakonyv/${await this.intezmeny()}`, await this.torzs(adat)));
  }
  async postaModosit(id: string, adat: any) {
    return firstValueFrom(this.http.patch<any>(`/api/iktatgato/postakonyv/${id}`, await this.torzs(adat)));
  }

  /* ── irat: részletek és életút ── */
  async irat(id: string) {
    return firstValueFrom(this.http.get<any>(await this.ut(`irat/${id}`)));
  }
  async iratModosit(id: string, adat: any) {
    return firstValueFrom(this.http.patch<any>(`/api/iktatgato/irat/${id}`, await this.torzs(adat)));
  }

  /* ── csatolmányok ── */
  /** A fájl multipart-ban megy; a mezőnév `file`, mert az app.js azt kapja el. */
  async csatolmanyFeltolt(fajl: File, adat: Record<string, any>) {
    const fd = new FormData();
    fd.append('file', fajl, fajl.name);
    for (const [k, v] of Object.entries(adat)) {
      if (v !== null && v !== undefined && v !== '') fd.append(k, String(v));
    }
    fd.append('felhasznalo', await this.ki());
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/csatolmany/${await this.intezmeny()}`, fd));
  }
  csatolmanyUrl(id: string) { return `/api/iktatgato/csatolmany/${id}/letoltes`; }
  async csatolmanyTorol(id: string) {
    return firstValueFrom(this.http.delete<any>(`/api/iktatgato/csatolmany/${id}`));
  }

  /* ── kapcsolatok, szerelés ── */
  async kapcsolatAd(adat: any) {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/kapcsolat/${await this.intezmeny()}`, await this.torzs(adat)));
  }
  async kapcsolatTorol(id: string) {
    return firstValueFrom(this.http.delete<any>(`/api/iktatgato/kapcsolat/${id}`));
  }
  async szerel(ugyirat: string, celUgyirat: string) {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/szereles/${await this.intezmeny()}`,
      await this.torzs({ ugyirat, cel_ugyirat: celUgyirat })));
  }

  /* ── törzsadatok ── */
  async ugyintezok() {
    return firstValueFrom(this.http.get<any[]>(await this.ut(`ugyintezok/${await this.intezmeny()}`)));
  }
  async partnerek(kereses = '') {
    return firstValueFrom(this.http.get<any>(await this.ut(`partnerek/${await this.intezmeny()}`, { kereses })));
  }
  async partnerFelvesz(adat: any) {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/partner/${await this.intezmeny()}`, await this.torzs(adat)));
  }

  /* ── kereső, riport ── */
  async kereses(q: string) {
    return firstValueFrom(this.http.get<any>(await this.ut(`kereses/${await this.intezmeny()}`, { q })));
  }
  async riport(ev?: number) {
    return firstValueFrom(this.http.get<any>(await this.ut(`riport/${await this.intezmeny()}`, { ev })));
  }

  /* ── irattár: kölcsönzés, selejtezés ── */
  async irattar() {
    return firstValueFrom(this.http.get<any>(await this.ut(`irattar/${await this.intezmeny()}`)));
  }
  async kolcsonoz(adat: any) {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/kolcsonzes/${await this.intezmeny()}`, await this.torzs(adat)));
  }
  async kolcsonzesVissza(id: string) {
    return firstValueFrom(this.http.patch<any>(`/api/iktatgato/kolcsonzes/${id}`, await this.torzs({})));
  }
  async selejtez(adat: any) {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/selejtezes/${await this.intezmeny()}`, await this.torzs(adat)));
  }
  async jegyzokonyv(id: string) {
    return firstValueFrom(this.http.get<any>(await this.ut(`selejtezes/${id}/tetelek`)));
  }
  /** Selejtezési eljárás: levéltári bejelentés (20/2012. EMMI r. 86. § (2)). */
  async selejtezesBejelent(id: string, adat: any) {
    return firstValueFrom(this.http.post<any>(`/api/iktatgato/selejtezes/${id}/bejelentes`, await this.torzs(adat)));
  }
  /** Selejtezési eljárás végrehajtása – a szerver csak a bejelentés + 30. naptól engedi. */
  async selejtezesVegrehajt(id: string, adat: any) {
    return firstValueFrom(this.http.post<any>(`/api/iktatgato/selejtezes/${id}/vegrehajtas`, await this.torzs(adat)));
  }
  async selejtezesVisszavon(id: string, indok: string) {
    return firstValueFrom(this.http.post<any>(`/api/iktatgato/selejtezes/${id}/visszavonas`, await this.torzs({ indok })));
  }

  /* ── SULINFORM-híd ── */
  async sulinformForrasok() {
    return firstValueFrom(this.http.get<any[]>(await this.ut(`sulinform/forrasok/${await this.intezmeny()}`)));
  }
  async sulinformTetelek(forras: string, csakIktatatlan = true) {
    return firstValueFrom(this.http.get<any>(await this.ut(
      `sulinform/tetelek/${await this.intezmeny()}`, { forras, csak_iktatatlan: csakIktatatlan ? 1 : 0 })));
  }
  async sulinformIktat(adat: any) {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/sulinform/iktat/${await this.intezmeny()}`, await this.torzs(adat)));
  }

  /* ── hivatali kapus szervezetek hivatalos névsora ──
   *  Országos lista (NISZ), telepítésenként EGY példány – ezért nincs benne intézmény. */
  async hivatalokAllapot() {
    return firstValueFrom(this.http.get<any>(await this.ut('hivatalok/allapot')));
  }
  async hivatalokSzinkron() {
    return firstValueFrom(this.http.post<any>('/api/iktatgato/hivatalok/szinkron', await this.torzs({})));
  }
  async hivatalKereses(q: string, limit = 25) {
    return firstValueFrom(this.http.get<any>(await this.ut('hivatalok/kereses', { q, limit })));
  }

  /* ── partnertörzs (szervezetek és személyek) ── */

  /**
   *  DUPLIKÁTUMOK. A tömeges feladás egy ideig minden címzetthez új partnert hozott
   *  létre; a kapcsolati kód és az azonosító adatok a RÉGI soron maradtak, a küldés
   *  viszont az újra hivatkozott. A hiba javítva – a keletkezett párokat itt lehet
   *  összevonni.
   */
  async partnerDuplikatumok() {
    return firstValueFrom(this.http.get<any>(await this.ut(`partner-duplikatum/${await this.intezmeny()}`)));
  }
  async partnerEgyesites(megtart: string, beolvaszt: string[]) {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/partner-egyesites/${await this.intezmeny()}`,
      await this.torzs({ megtart, beolvaszt })));
  }

  async partnertorzs(szuro: { kereses?: string; tipus?: string; inaktiv?: number } = {}) {
    return firstValueFrom(this.http.get<any[]>(await this.ut(`partnertorzs/${await this.intezmeny()}`, szuro)));
  }
  async partnerModosit(id: string, adat: any) {
    return firstValueFrom(this.http.patch<any>(`/api/iktatgato/partner/${id}`, await this.torzs(adat)));
  }
  async partnerImport(forras: 'allomany' | 'ber_alkalmazott') {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/partner-import/${await this.intezmeny()}`, await this.torzs({ forras })));
  }

  /* ── példányok, átadás-átvétel, feladatok ── */
  async peldanyok(szuro: Record<string, any> = {}) {
    return firstValueFrom(this.http.get<any[]>(await this.ut(`peldanyok/${await this.intezmeny()}`, szuro)));
  }
  async peldanyLetrehoz(adat: any) {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/peldany/${await this.intezmeny()}`, await this.torzs(adat)));
  }
  async peldanyModosit(id: string, adat: any) {
    return firstValueFrom(this.http.patch<any>(`/api/iktatgato/peldany/${id}`, await this.torzs(adat)));
  }
  async atadasok(szuro: Record<string, any> = {}) {
    return firstValueFrom(this.http.get<any[]>(await this.ut(`atadasok/${await this.intezmeny()}`, szuro)));
  }
  async atad(adat: any) {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/atadas/${await this.intezmeny()}`, await this.torzs(adat)));
  }
  async atvetel(id: string, adat: any = {}) {
    return firstValueFrom(this.http.patch<any>(`/api/iktatgato/atadas/${id}`, await this.torzs(adat)));
  }
  async feladatok(szuro: Record<string, any> = {}) {
    return firstValueFrom(this.http.get<any[]>(await this.ut(`feladatok/${await this.intezmeny()}`, szuro)));
  }
  async feladatKiad(adat: any) {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/feladat/${await this.intezmeny()}`, await this.torzs(adat)));
  }
  async feladatModosit(id: string, adat: any) {
    return firstValueFrom(this.http.patch<any>(`/api/iktatgato/feladat/${id}`, await this.torzs(adat)));
  }
  async munkaasztal() {
    return firstValueFrom(this.http.get<any>(await this.ut(`munkaasztal/${await this.intezmeny()}`)));
  }

  /* ── tömeges kiküldés ── */
  async kuldesForrasok(ev?: number, ho?: number) {
    return firstValueFrom(this.http.get<any[]>(
      await this.ut(`kuldes/forrasok/${await this.intezmeny()}`, { ev, ho })));
  }
  /** Címzett-kereső az egyedi kiküldéshez: dolgozó, tanuló, gondviselő, partner. */
  async kuldesSzemelyek(q: string, kor?: string) {
    return firstValueFrom(this.http.get<any>(
      await this.ut(`kuldes/szemelyek/${await this.intezmeny()}`, { q, kor })));
  }
  async kuldesTetelek(forras: string, ev?: number, ho?: number) {
    return firstValueFrom(this.http.get<any>(
      await this.ut(`kuldes/tetelek/${await this.intezmeny()}`, { forras, ev, ho })));
  }
  async kotegek() {
    return firstValueFrom(this.http.get<any[]>(await this.ut(`kuldes/kotegek/${await this.intezmeny()}`)));
  }
  async koteg(id: string) {
    return firstValueFrom(this.http.get<any>(await this.ut(`kuldes/koteg/${id}`)));
  }
  async kotegLetrehoz(adat: any) {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/kuldes/koteg/${await this.intezmeny()}`, await this.torzs(adat)));
  }
  /**
   *  ZIP-ből köteg: a kész PDF-eket a rendszer a fájlnevekben lévő név alapján osztja
   *  szét a dolgozók között. Egy kérés, mert a párosítás eredményét úgyis látni kell.
   */
  async kotegZipbol(zip: File, adat: any) {
    const fd = new FormData();
    fd.append('file', zip, zip.name);
    for (const [k, v] of Object.entries(adat || {})) {
      if (v !== undefined && v !== null && v !== '') fd.append(k, String(v));
    }
    fd.append('felhasznalo', await this.ki());
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/kuldes/koteg-zip/${await this.intezmeny()}`, fd));
  }

  /** A riportkészítő tölti fel a dolgozónkénti bérjegyzék-PDF-et. */
  async kotegDokumentum(kotegId: string, azonosito: string, pdf: Blob, fajlnev = 'berjegyzek.pdf') {
    const fd = new FormData();
    fd.append('file', pdf, fajlnev);
    fd.append('azonosito', azonosito);
    fd.append('felhasznalo', await this.ki());
    return firstValueFrom(this.http.post<any>(`/api/iktatgato/kuldes/koteg/${kotegId}/dokumentum`, fd));
  }
  /**
   *  Dokumentum feltöltése EGY köteg-tételhez, a tétel azonosítójával.
   *
   *  Az egyedi kiküldésnél a PDF-et a felhasználó tölti fel a kiküldés képernyőjén
   *  (nincs riportkészítő, ami megtenné helyette), és ott a köteg TÉTELE a kapaszkodó,
   *  nem a forrás-azonosító.
   */
  async kotegTetelDokumentum(kotegId: string, tetelId: string, fajl: File) {
    const fd = new FormData();
    fd.append('file', fajl, fajl.name || 'irat.pdf');
    fd.append('tetel_id', tetelId);
    fd.append('felhasznalo', await this.ki());
    return firstValueFrom(this.http.post<any>(`/api/iktatgato/kuldes/koteg/${kotegId}/dokumentum`, fd));
  }
  async kotegLepes(id: string, lepes: 'alairas' | 'iktatas' | 'kuldes', adat: any = {}) {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/kuldes/koteg/${id}/${lepes}`, await this.torzs(adat)));
  }
  kotegTetelUrl(id: string) { return `/api/iktatgato/kuldes/tetel/${id}/letoltes`; }

  /* ── BKSZ (Biztonságos Kézbesítési Szolgáltatás) ── */
  async bkszTanusitvanyok() {
    return firstValueFrom(this.http.get<any[]>(await this.ut(`bksz/tanusitvany/${await this.intezmeny()}`)));
  }
  async bkszTanusitvanyKijelol(id: string) {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/bksz/tanusitvany/${await this.intezmeny()}`, await this.torzs({ id })));
  }
  async bkszTanusitvanyFeltolt(fajl: File, jelszo: string) {
    const fd = new FormData();
    fd.append('file', fajl, fajl.name);
    fd.append('jelszo', jelszo || '');
    fd.append('felhasznalo', await this.ki());
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/bksz/tanusitvany-feltoltes/${await this.intezmeny()}`, fd));
  }
  async bkszSajat() {
    return firstValueFrom(this.http.get<any>(await this.ut(`bksz/sajat/${await this.intezmeny()}`)));
  }
  async bkszSajatMent(adat: any) {
    return firstValueFrom(this.http.put<any>(
      `/api/iktatgato/bksz/sajat/${await this.intezmeny()}`, await this.torzs(adat)));
  }
  async bkszTeszt(url?: string) {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/bksz/teszt/${await this.intezmeny()}`, await this.torzs({ url })));
  }
  async bkszKapcsolatiKod(adat: any) {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/bksz/kapcsolati-kod/${await this.intezmeny()}`, await this.torzs(adat)));
  }
  /*  A DOLGOZÓK kapcsolati kódjai egy listában – lekéréshez és kézi megadáshoz.
   *  Természetes személynek csak kapcsolati kóddal lehet küldeni; enélkül a
   *  bérjegyzék-kiküldés személyenként akad el. */
  async bkszKapcsolatiKodok() {
    return firstValueFrom(this.http.get<any[]>(
      `/api/iktatgato/bksz/kapcsolati-kodok/${await this.intezmeny()}`));
  }
  async bkszKapcsolatiKodMentes(partnerId: string, kod: string) {
    return firstValueFrom(this.http.put<any>(
      `/api/iktatgato/bksz/kapcsolati-kod/${await this.intezmeny()}`,
      await this.torzs({ partner_id: partnerId, kapcsolati_kod: kod })));
  }
  async bkszNyers(xml: string, soapAction = '', url?: string) {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/bksz/nyers/${await this.intezmeny()}`,
      await this.torzs({ xml, soap_action: soapAction, url })));
  }

  /* ── szabályok, jogosultság ── */
  async szabalyok() {
    return firstValueFrom(this.http.get<any[]>(await this.ut(`szabalyok/${await this.intezmeny()}`)));
  }
  async szabalyMent(adat: any) {
    return firstValueFrom(this.http.put<any>(
      `/api/iktatgato/szabaly/${await this.intezmeny()}`, await this.torzs(adat)));
  }
  async jogosultsagok() {
    return firstValueFrom(this.http.get<any>(await this.ut(`jogosultsagok/${await this.intezmeny()}`)));
  }
  async jogosultsagAd(adat: any) {
    return firstValueFrom(this.http.post<any>(
      `/api/iktatgato/jogosultsag/${await this.intezmeny()}`, await this.torzs(adat)));
  }
  async jogosultsagVon(id: string) {
    return firstValueFrom(this.http.delete<any>(`/api/iktatgato/jogosultsag/${id}`));
  }
}
